// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import {useLocation} from 'react-router-dom';
import {WORKBOARD, WORKBOARD_TEMPLATES} from 'Common/constants/routes';
import {EXISTING_WORKBOARD_TEMPLATE, JOB, NEW_WORKBOARD_TEMPLATE} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {redirect} from 'Src/ui-core/utils/redirect-utils';
import {saveWorkboardTemplate, saveWorkboardV2, saveExistingWorkboardV2} from '../../utils/workboard-save';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {deleteWorkboard} from 'Services/work-board-service';
import {generateWorkboardPdf} from 'Ui/features/onlabor/workboards/generate-workboard-pdf';
import {v4} from 'uuid';
import {DUPLICATE_ENTITY_TYPES, normalizeName, parseDuplicateConflict} from '../../utils/workboard-conflict-utils';
import {getWorkboardDateTimeKey} from '../../utils/workboard-data-utils';
import {compareWorkboards} from 'OnLabor/workboard/utils/setup-workboard';
import {GOLF_FLEET_TO_EQUIPMENT, PRO_GOLF_PREVIEW_FIX} from 'Common/constants/isg-toggles';
import {replaceLocation} from 'Src/ui-core/utils/window-location-utils';

function isEmptyResult(result) {
    return result?.workboardResult === null || result?.workboardResult === undefined || Object.keys(result.workboardResult).length === 0;
}

function getDuplicateConflict(source) {
    if (source.isDuplicateName) {
        return {
            isDuplicateName: source.isDuplicateName,
            duplicateEntityType: source.duplicateEntityType
        };
    }

    return parseDuplicateConflict(source.error || source);
}

function markDuplicateIfNeeded(markDuplicateName, duplicateConflict) {
    if (duplicateConflict.isDuplicateName) {
        markDuplicateName(duplicateConflict.duplicateEntityType);
    }
}

function getTemplateErrorToast(translations) {
    return {
        message: translations.ONLINK_TEMPLATE_DUPLICATED_NAME_MESSAGE,
        title: translations.ONLINK_TEMPLATE_SAVED_ERROR,
        persist: false,
        type: TOAST_TYPE.ERROR
    };
}

function showWorkboardErrorToast(addToast, translations) {
    addToast({
        message: translations.ONLINK_WORKBOARD_DUPLICATED_DATE_TIME_MESSAGE,
        persist: true,
        title: translations.ONLINK_WORKBOARD_SAVED_ERROR,
        type: TOAST_TYPE.ERROR
    });
}

function getGenericErrorToast(translations) {
    return {
        message: translations.REFRESH_BROWSER_AND_TRY_AGAIN,
        title: translations.ERROR_OCCURRED_TITLE,
        persist: false,
        type: TOAST_TYPE.ERROR
    };
}

function showWorkboardSuccessToast(addToast, translations, isDraft) {
    addToast({
        message: isDraft ? translations.ONLINK_WORKBOARD_SAVED : translations.ONLINK_WORKBOARD_PUBLISHED,
        persist: false,
        title: translations.SUCCESS,
        type: TOAST_TYPE.SUCCESS
    });
}

async function saveWorkboardByMode({
    creationFlow,
    isFleetToEquipmentEnabled,
    membership,
    operators,
    rowOrder,
    templates,
    workboardData,
    isDraft
}) {
    const workboardCreationFlow = creationFlow || membership.properties.workboard_creation_flow || JOB;

    if (workboardData.isEditing) {
        return saveExistingWorkboardV2({
            membership,
            workboardData,
            rowOrder,
            workboardCreationFlow,
            workboardId: workboardData.workboardId,
            isDraft,
            wasDraft: workboardData.isDraft,
            useEquipmentManager: isFleetToEquipmentEnabled
        });
    }

    return saveWorkboardV2({
        membership,
        operators,
        templates,
        workboardData,
        rowOrder,
        workboardCreationFlow,
        isDraft,
        useEquipmentManager: isFleetToEquipmentEnabled
    });
}

function resetWizard(resetFn, location, history) {
    resetFn();
    if (location.key) {
        history.goBack();
    } else if (location.pathname.includes(WORKBOARD_TEMPLATES)) {
        redirect(WORKBOARD_TEMPLATES, history);
    } else {
        redirect(WORKBOARD, history);
    }
}

function handleWorkboardSaveCompletion({
    cb,
    workboardResult,
    location,
    history,
    resetSetupState
}) {
    if (cb) {
        cb(workboardResult?.workboardId);
        return;
    }

    resetWizard(resetSetupState, location, history);
}

function showToasts(addToast, calledFromDialog, closeDialog, translations, location, history, resetSetupState, isNew) {
    const successMessage = isNew ? translations.ONLINK_WORKBOARD_CANCELED : translations.ONLINK_CHANGES_CANCELED;
    const errorTitle = isNew ? translations.ONLINK_WORKBOARD_CANCELED_ERROR : translations.ONLINK_CHANGES_CANCELED_ERROR;

    try {
        resetWizard(resetSetupState, location, history);
        addToast({
            title: translations.SUCCESS,
            message: successMessage,
            persist: false,
            type: TOAST_TYPE.SUCCESS
        });
    } catch {
        addToast({
            title: errorTitle,
            message: translations.REFRESH_BROWSER_AND_TRY_AGAIN,
            persist: false,
            type: TOAST_TYPE.ERROR
        });
    } finally {
        if (calledFromDialog) {
            closeDialog();
        }
    }
}

function openCancelConfirmationDialog({
    isNew,
    openConfirmation,
    closeConfirmation,
    resetSetupState,
    location,
    history,
    addToast,
    translations,
    initialWorkboard,
    currentWorkboard
}) {
    const {
        isEditing,
        isDraft,
        initialSelectedJobs,
        initialSelectedOperators,
        initialWorkItems,
        workboardId,
        ...toCompare
    } = currentWorkboard;
    const hasChanged = compareWorkboards(initialWorkboard, toCompare);
    const confirmationMessage = isNew ? translations.ONLINK_DISCARD_NEW_WORKBOARD : translations.ONLINK_DISCARD_WORKBOARD_CHANGES;

    if (hasChanged) {
        openConfirmation({
            onCancel: closeConfirmation,
            showLoadingIcon: true,
            message: confirmationMessage,
            title: translations.DISCARD_CHANGES,
            cancelTitle: translations.CANCEL,
            continueTitle: translations.DISCARD_CHANGES,
            onContinue() {
                showToasts(addToast, true, closeConfirmation, translations, location, history, resetSetupState, isNew);
            }
        });
    } else {
        showToasts(addToast, false, closeConfirmation, translations, location, history, resetSetupState, isNew);
    }
}

function handleNext(activeStep, stepsLength, setActiveStep) {
    if (activeStep < stepsLength) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
}

function handleBack(activeStep, setActiveStep) {
    if (activeStep > 0) {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
}

function handleDelete({
    workboardData,
    openConfirmation,
    closeConfirmation,
    resetSetupState,
    location,
    history,
    addToast,
    translations
}) {
    openConfirmation({
        message: translations.ONLINK_DELETE_WORKBOARD_CONFIRMATION,
        title: translations.ONLINK_DELETE_WORKBOARD,
        cancelTitle: translations.CANCEL,
        continueTitle: translations.CONFIRM_DELETE,
        onCancel: closeConfirmation,
        async onContinue() {
            try {
                await deleteWorkboard(workboardData.workboardId);
                resetWizard(resetSetupState, location, history);
                addToast({
                    title: translations.SUCCESS,
                    message: translations.ONLINK_WORKBOARD_DELETED,
                    persist: false,
                    type: TOAST_TYPE.SUCCESS
                });
            } catch {
                addToast({
                    title: translations.ONLINK_WORKBOARD_DELETED_ERROR,
                    message: translations.REFRESH_BROWSER_AND_TRY_AGAIN,
                    persist: false,
                    type: TOAST_TYPE.ERROR
                });
            } finally {
                closeConfirmation();
            }
        },
        showLoadingIcon: true,
        translations
    });
}

async function generateAndOpenPdf({
    printWindow,
    operators,
    workboardId,
    membership,
    translations
}) {
    const teamMembersMap = new Map((operators ?? []).map((teamMember) => [teamMember.id, teamMember]));

    const workboardPDF = await generateWorkboardPdf(
        workboardId,
        membership.membershipId,
        translations,
        teamMembersMap,
        membership.orgId
    );

    workboardPDF.autoPrint({
        variant: 'non-conform'
    });

    if (printWindow && !printWindow.closed) {
        printWindow.location.replace(workboardPDF.output('bloburl'));
    }
}

async function executePrint({
    handleSaveAsDraft,
    printWindow,
    operators,
    workboardData,
    membership,
    translations,
    setIsLoading,
    handleLoadingSetup
}) {
    let printPromise;

    await handleSaveAsDraft((id) => {
        printPromise = generateAndOpenPdf({
            printWindow,
            operators,
            workboardId: id ?? workboardData.workboardId,
            membership,
            translations
        });
    }, workboardData.isDraft || !workboardData.isEditing);

    if (printPromise) {
        setIsLoading(true);
        handleLoadingSetup(true);

        await printPromise;
    } else if (printWindow && !printWindow.closed) {
        printWindow.close();
    }
}

function regenerateWorkItemIds(workItems) {
    return Object.entries(workItems).reduce((acc, [key, item]) => {
        const {
            workboardTemplateWorkItemId,
            savedJobId,
            workboardTemplateWorkboardId,
            ...rest
        } = item;

        acc[key] = {
            ...rest,
            workItemId: v4()
        };
        return acc;
    }, {});
}

function getWizardFlags(type, workboardData) {
    const isTemplate = type === EXISTING_WORKBOARD_TEMPLATE || type === NEW_WORKBOARD_TEMPLATE;
    const hasTemplateDuplicate = workboardData.nameValidation?.duplicateEntityType === DUPLICATE_ENTITY_TYPES.TEMPLATE &&
        Boolean(workboardData.nameValidation?.isDuplicate);

    return {
        isTemplate,
        hasTemplateDuplicate,
        isTemplateDuplicateActive: isTemplate && hasTemplateDuplicate,
        isDisabled: workboardData.details.name === '' || workboardData.details.date === '' || isTemplate && hasTemplateDuplicate,
        showSaveDraftBtn: !workboardData.isEditing || workboardData.isDraft === true
    };
}

function stopSaving(setIsLoading, handleIsSaving, handleLoadingSetup) {
    setIsLoading(false);
    handleIsSaving(false);
    handleLoadingSetup(false);
}

async function saveDraftInternal({
    cb,
    isDraft,
    saveArgs,
    applyDuplicateConflict,
    addToast,
    translations,
    location,
    history,
    resetSetupState
}) {
    const saveResult = await saveWorkboardByMode(saveArgs);

    const hasResultError = saveResult?.hasError === true || isEmptyResult(saveResult);

    if (hasResultError) {
        applyDuplicateConflict(saveResult);

        if (saveResult.isDuplicateName) {
            showWorkboardErrorToast(addToast, translations);
        } else {
            addToast(getGenericErrorToast(translations));
        }

        return;
    }

    showWorkboardSuccessToast(addToast, translations, isDraft);
    handleWorkboardSaveCompletion({
        cb,
        workboardResult: saveResult.workboardResult,
        location,
        history,
        resetSetupState
    });
}

async function publishTemplateInternal({
    workboardData,
    rowOrder,
    creationFlow,
    membership,
    applyDuplicateConflict,
    addToast,
    errorToast,
    translations,
    resetSetupState,
    location,
    history
}) {
    const saveResult = await saveWorkboardTemplate({
        ...workboardData,
        rowOrder,
        creationFlow
    }, membership);

    if (saveResult.hasError) {
        applyDuplicateConflict(saveResult);
        addToast(saveResult.isDuplicateName ? errorToast : getGenericErrorToast(translations));
        return;
    }

    addToast({
        title: translations.SUCCESS,
        message: translations.ONLINK_TEMPLATE_SAVED,
        persist: false,
        type: TOAST_TYPE.SUCCESS
    });
    resetWizard(resetSetupState, location, history);
}

function handleNextAction({
    isTemplate,
    activeStep,
    touchTemplateValidation,
    hasTemplateDuplicate,
    addToast,
    translations,
    stepsLength,
    setActiveStep
}) {
    if (!isTemplate || activeStep !== 0) {
        handleNext(activeStep, stepsLength, setActiveStep);
        return;
    }

    touchTemplateValidation();

    if (hasTemplateDuplicate) {
        addToast(getTemplateErrorToast(translations));
        return;
    }

    handleNext(activeStep, stepsLength, setActiveStep);
}

function useWorkboardWizardActions({
    activeStep,
    creationFlow,
    handleLoadingSetup,
    handleDetailsField,
    handleNameValidation,
    handleIsSaving,
    handleSetCopyWorkboardMode,
    handleSetWorkItems,
    history,
    isgToggles,
    isMobile,
    membership,
    openConfirmation,
    closeConfirmation,
    openWorkboardPreviewDialog,
    operators,
    resetSetupState,
    rowOrder,
    setActiveStep,
    stepsLength,
    templates,
    type,
    translations,
    workboardData
}) {
    const {addToast} = useToast();
    const location = useLocation();
    const isPreviewFixEnabled = isgToggles && isgToggles[PRO_GOLF_PREVIEW_FIX];
    const [isLoading, setIsLoading] = React.useState(false);

    const {
        hasTemplateDuplicate,
        isDisabled,
        isTemplate,
        isTemplateDuplicateActive,
        showSaveDraftBtn
    } = getWizardFlags(type, workboardData);

    const markDuplicateName = React.useCallback((duplicateEntityType) => {
        handleNameValidation({
            isTouched: true,
            isDuplicate: true,
            duplicateEntityType,
            forcedDuplicateName: normalizeName(workboardData.details.name),
            forcedDuplicateDateTime: duplicateEntityType === DUPLICATE_ENTITY_TYPES.WORKBOARD ?
                getWorkboardDateTimeKey(workboardData.details.date) :
                null
        });
        setActiveStep(0);
    }, [handleNameValidation, setActiveStep, workboardData.details.date, workboardData.details.name]);

    const applyDuplicateConflict = React.useCallback((source) => {
        const duplicateConflict = getDuplicateConflict(source);

        markDuplicateIfNeeded(markDuplicateName, duplicateConflict);
    }, [markDuplicateName]);

    const touchTemplateValidation = React.useCallback(() => {
        handleNameValidation({
            duplicateEntityType: DUPLICATE_ENTITY_TYPES.TEMPLATE,
            isTouched: true
        });
    }, [handleNameValidation]);

    const handlePublishTemplate = React.useCallback(async () => {
        touchTemplateValidation();

        if (hasTemplateDuplicate) {
            addToast(getTemplateErrorToast(translations));
            return;
        }

        setIsLoading(true);
        handleIsSaving(true);
        const errorToast = getTemplateErrorToast(translations);

        try {
            await publishTemplateInternal({
                workboardData,
                rowOrder,
                creationFlow,
                membership,
                applyDuplicateConflict,
                addToast,
                errorToast,
                translations,
                resetSetupState,
                location,
                history
            });
        } catch (error) {
            applyDuplicateConflict(error);
            addToast(parseDuplicateConflict(error).isDuplicateName ? errorToast : getGenericErrorToast(translations));
        } finally {
            stopSaving(setIsLoading, handleIsSaving, handleLoadingSetup);
        }
    }, [addToast, applyDuplicateConflict, creationFlow, handleIsSaving, handleLoadingSetup, hasTemplateDuplicate, history, location, membership, resetSetupState, rowOrder, touchTemplateValidation, translations, workboardData]);

    const handleSaveAsDraft = React.useCallback(async (cb, isDraft = true) => {
        setIsLoading(true);
        handleIsSaving(true);

        try {
            await saveDraftInternal({
                cb,
                isDraft,
                saveArgs: {
                    creationFlow,
                    isFleetToEquipmentEnabled: Boolean(isgToggles?.[GOLF_FLEET_TO_EQUIPMENT]),
                    membership,
                    operators,
                    rowOrder,
                    templates,
                    workboardData,
                    isDraft
                },
                applyDuplicateConflict,
                addToast,
                translations,
                location,
                history,
                resetSetupState
            });
        } catch (error) {
            applyDuplicateConflict(error);

            if (parseDuplicateConflict(error).isDuplicateName) {
                showWorkboardErrorToast(addToast, translations);
            } else {
                addToast(getGenericErrorToast(translations));
            }
        } finally {
            stopSaving(setIsLoading, handleIsSaving, handleLoadingSetup);
        }
    }, [addToast, applyDuplicateConflict, creationFlow, handleIsSaving, handleLoadingSetup, history, isgToggles, location, membership, operators, resetSetupState, rowOrder, templates, translations, workboardData]);

    const handlePublish = React.useCallback(async () => {
        await handleSaveAsDraft(undefined, false);
    }, [handleSaveAsDraft]);

    const handleCopy = React.useCallback(async () => {
        setActiveStep(() => 0);
        handleLoadingSetup(true);
        try {
            await handleSaveAsDraft(() => {
                handleSetCopyWorkboardMode();
                handleSetWorkItems(regenerateWorkItemIds(workboardData.workItems));
                handleDetailsField({
                    field: 'name',
                    value: `${workboardData.details.name}-${translations.COPY}`
                });
                handleDetailsField({
                    field: 'date',
                    value: ''
                });
                history.replace(`${WORKBOARD}/new`, {
                    preserveWorkboardCopy: true
                });
            }, workboardData.isDraft || !workboardData.isEditing);
        } finally {
            handleLoadingSetup(false);
        }
    }, [handleDetailsField, handleLoadingSetup, handleSaveAsDraft, handleSetCopyWorkboardMode, handleSetWorkItems, history, setActiveStep, translations.COPY, workboardData.details.name, workboardData.isDraft, workboardData.isEditing, workboardData.workItems]);

    const handlePreview = React.useCallback(async () => {
        const previewWindow = isPreviewFixEnabled && !isMobile ?
            window.open(`${window.props.onLinkLegacyUri}/labor/workboard/presentation/${membership.membershipId}?isLoading=true`, '_blank') :
            null;

        if (previewWindow) {
            previewWindow.opener = null;
        }

        handleLoadingSetup(true);
        let callbackExecuted = false;

        try {
            await handleSaveAsDraft((workboardId) => {
                callbackExecuted = true;
                const previewUrl = `${window.props.onLinkLegacyUri}/labor/workboard/presentation/${membership.membershipId}/${workboardId}?showToast=true`;
                const redirectEditUrl = `${window.props.onLinkLegacyUri}/labor/workboard/${workboardId}`;

                if (isMobile) {
                    openWorkboardPreviewDialog({
                        previewUrl,
                        translations
                    });
                    return;
                }

                if (previewWindow && !previewWindow.closed) {
                    previewWindow.location.replace(previewUrl);
                } else if (!isPreviewFixEnabled) {
                    window.open(previewUrl);
                }
                replaceLocation(redirectEditUrl);
            }, workboardData.isDraft || !workboardData.isEditing);

            if (!callbackExecuted && previewWindow && !previewWindow.closed) {
                previewWindow.close();
            }
        } catch {
            if (previewWindow && !previewWindow.closed) {
                previewWindow.close();
            }
        } finally {
            handleLoadingSetup(false);
        }
    }, [handleLoadingSetup, handleSaveAsDraft, isMobile, isPreviewFixEnabled, membership.membershipId, openWorkboardPreviewDialog, translations, workboardData.isDraft, workboardData.isEditing]);

    const handlePrintWorkboard = React.useCallback(async () => {
        if (!workboardData.workboardId) {
            addToast({
                title: translations.ERROR_OCCURRED_TITLE,
                message: translations.REFRESH_BROWSER_AND_TRY_AGAIN,
                persist: false,
                type: TOAST_TYPE.ERROR
            });
            return;
        }

        const printWindow = window.open('about:blank', '_blank');

        if (printWindow) {
            printWindow.opener = null;
        }

        setIsLoading(true);
        handleLoadingSetup(true);

        try {
            await executePrint({
                handleSaveAsDraft,
                printWindow,
                operators,
                workboardData,
                membership,
                translations,
                setIsLoading,
                handleLoadingSetup
            });
        } catch {
            if (printWindow && !printWindow.closed) {
                printWindow.close();
            }
            addToast({
                title: translations.ERROR_OCCURRED_TITLE,
                message: translations.REFRESH_BROWSER_AND_TRY_AGAIN,
                persist: false,
                type: TOAST_TYPE.ERROR
            });
        } finally {
            setIsLoading(false);
            handleLoadingSetup(false);
        }
    }, [addToast, handleLoadingSetup, handleSaveAsDraft, membership, operators, translations, workboardData]);

    const handleAction = React.useCallback((action, initialWorkboard, currentWorkboard) => {
        switch (action) {
            case 'NEXT':
                handleNextAction({
                    isTemplate,
                    activeStep,
                    touchTemplateValidation,
                    hasTemplateDuplicate,
                    addToast,
                    translations,
                    stepsLength,
                    setActiveStep
                });
                break;
            case 'BACK':
                handleBack(activeStep, setActiveStep);
                break;
            case 'CANCEL':
                openCancelConfirmationDialog({
                    isNew: !workboardData.isEditing,
                    openConfirmation,
                    closeConfirmation,
                    resetSetupState,
                    location,
                    history,
                    addToast,
                    translations,
                    initialWorkboard,
                    currentWorkboard
                });
                break;
            case 'DELETE':
                handleDelete({
                    workboardData,
                    openConfirmation,
                    closeConfirmation,
                    resetSetupState,
                    location,
                    history,
                    addToast,
                    translations
                });
                break;
            default:
                break;
        }
    }, [activeStep, addToast, closeConfirmation, hasTemplateDuplicate, history, isTemplate, location, openConfirmation, resetSetupState, setActiveStep, stepsLength, touchTemplateValidation, translations, workboardData]);

    return {
        handleAction,
        handlePublishTemplate,
        handleSaveAsDraft,
        handlePublish,
        handleCopy,
        handlePreview,
        handlePrintWorkboard,
        isLoading,
        isDisabled,
        isTemplateDuplicateActive,
        showSaveDraftBtn
    };
}

export default useWorkboardWizardActions;
